import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  ArrowLeftRight,
  BarChart3,
  Banknote,
  Boxes,
  Briefcase,
  Building2,
  Calculator,
  CalendarCheck,
  CalendarDays,
  BookOpen,
  ChevronRight,
  ChevronsLeft,
  Contact,
  CreditCard,
  FileSignature,
  FileClock,
  FileStack,
  FileText,
  FolderOpen,
  Landmark,
  Layers,
  LayoutDashboard,
  ListTree,
  Package,
  Plus,
  ReceiptText,
  RefreshCw,
  Scale,
  Settings as SettingsIcon,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Users,
  Wallet,
  Warehouse,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Logo } from '@/components/ui/Logo';
import {
  Sidebar as ShadcnSidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarSeparator,
  useSidebar,
} from '@/components/shadcn/sidebar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/shadcn/popover';
import { AccountMenu } from './AccountMenu';
import { SidebarCredits } from './SidebarCredits';
import { DraftsBadge } from './DraftsBadge';
import { SidebarControls } from './SidebarControls';
import { OrgSwitcher } from './OrgSwitcher';
import { useI18n } from '@/contexts/I18nContext';
import { useAppMode } from '@/contexts/AgentModeContext';
import { useAuth } from '@/contexts/AuthContext';
import { can } from '@/lib/permissions';
import type { Capability } from '@/lib/permissions';
import { createTargetFor } from '@/lib/createRoutes';
import { cn } from '@/lib/utils';

/** A destination. The leaves of the tree, and the only things with a URL. */
interface NavLeaf {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  allow?: Capability;
  /** Hidden when the organization has this product switched off. */
  module?: string;
  /**
   * Show the count of waiting changes after the label.
   *
   * A flag rather than a component or a number, because the rail is a data
   * table and everything else in it is data. What the flag turns on is
   * `DraftsBadge`, which does its own fetching and its own capability check, so
   * a role that may not read drafts sees the row disappear from `allow`
   * long before the badge would have had anything to say.
   */
  badge?: boolean;
  /*
    There is no `createTo` here on purpose. Where "new X" lives is a fact about
    the PAGE, not about the rail, and it is written down once in
    lib/createRoutes.ts keyed by this same `to`. Copying it into this array would
    mean two lists to keep in step, and the one in the rail would be the one
    nobody updated when a page moved its form.
  */
}

/** A shelf. Has no URL of its own: opening it is what it does. */
interface NavParent {
  /** Both the label and the key its open state is remembered under. */
  labelKey: string;
  icon: LucideIcon;
  module?: string;
  children: NavLeaf[];
}

type NavNode = NavLeaf | NavParent;

const isParent = (node: NavNode): node is NavParent => 'children' in node;

/**
 * THE RAIL IS A TREE NOW, AND THAT IS THE WHOLE CHANGE.
 *
 * It used to be seven groups whose headings were captions rather than controls:
 * twenty-seven destinations, all pinned to the rail at once, every one of them
 * shouting at the same volume whether or not you were anywhere near it. On a
 * laptop the last rows sat under the footer and had to be scrolled to.
 *
 * What we took from Zoho Books, which is the reference the owner sent:
 *
 *   · A SHORT TOP LEVEL. Ten rows, and nine of them fit on any screen. You read
 *     a list of ten. You scan a list of twenty-seven and give up.
 *   · A HEADING YOU CAN OPEN. Their Sales and Purchases are rows with a chevron,
 *     not captions, so the shelf you are not using costs one line instead of
 *     seven.
 *   · ONE OPEN AT A TIME IN PRACTICE, because the only shelf that opens itself
 *     is the one holding the page you are on.
 *   · A `+` ON THE ROW. Their Expenses row carries a create shortcut, so the
 *     most common thing you do with a list is one click from the navigation
 *     rather than a page load and then a button.
 *
 * What we deliberately did not take: their palette. Railit is monochrome by
 * decision (brand/palette.json, and `npm run brand:check` fails if a brand hex
 * appears anywhere), so the accent on the active row is `--sidebar-primary`,
 * whatever that file says it is, and never a colour written here.
 *
 * NO DESTINATION MOVED. Every `to` below was in the old array and every one is
 * still reachable; what changed is which shelf it sits on and whether that shelf
 * is open. The gates are unchanged too, only relocated: the old Inventory group
 * carried `module: 'inventory'` for all four of its items, so those four now
 * carry it themselves.
 */
const NAV: NavNode[] = [
  /* Never gated: whatever else is switched off, there is a front door. */
  { to: '/dashboard', labelKey: 'dashboard', icon: LayoutDashboard },
  {
    /*
      What you sell and how much of it there is, on one shelf.
      Products deliberately carries NO module key while the four stock screens
      do: a contractor with Inventory switched off still sells "Site
      supervision, 40 hours" and still wants it saved. The module gates stock,
      not the catalogue. That tenant sees this shelf collapse to a single
      Products row, which is what the one-child rule below is for.
    */
    labelKey: 'inventory',
    icon: Boxes,
    children: [
      { to: '/items', labelKey: 'products', icon: Package, allow: 'items.read' },
      { to: '/inventory/stock', labelKey: 'stock_levels', icon: Layers, allow: 'stock.read', module: 'inventory' },
      { to: '/inventory/movements', labelKey: 'stock_movements', icon: ArrowLeftRight, allow: 'stock.read', module: 'inventory' },
      { to: '/inventory/adjustments', labelKey: 'adjustments', icon: Scale, allow: 'stock.read', module: 'inventory' },
      { to: '/inventory/warehouses', labelKey: 'warehouses', icon: Warehouse, allow: 'stock.read', module: 'inventory' },
    ],
  },
  {
    labelKey: 'sales',
    icon: ShoppingCart,
    children: [
      /* Distinct icons on purpose. Invoices and Quotations sat next to each
         other with the same FileText glyph and near-identical labels, so the
         only way to tell them apart was to read carefully, which is the one
         thing nobody does with a sidebar. */
      { to: '/invoices', labelKey: 'invoices', icon: FileText, allow: 'invoices.read', module: 'finance' },
      { to: '/quotations', labelKey: 'quotations', icon: FileClock, allow: 'quotations.read', module: 'crm' },
      { to: '/customers', labelKey: 'customers', icon: Users, allow: 'customers.read', module: 'crm' },
      { to: '/contracts', labelKey: 'contracts', icon: FileSignature, allow: 'contracts.read', module: 'crm' },
    ],
  },
  {
    labelKey: 'purchases',
    icon: ShoppingBag,
    children: [
      { to: '/bills', labelKey: 'bills', icon: ReceiptText, allow: 'bills.read', module: 'finance' },
      { to: '/expenses', labelKey: 'expenses', icon: Wallet, allow: 'expenses.read', module: 'finance' },
      { to: '/vendors', labelKey: 'vendors', icon: Building2, allow: 'vendors.read' },
    ],
  },
  {
    /* Money MOVING, as opposed to the documents that promise it. */
    labelKey: 'money',
    icon: Banknote,
    children: [
      { to: '/payments', labelKey: 'payments', icon: CreditCard, allow: 'payments.read', module: 'finance' },
      { to: '/accounting/bank-accounts', labelKey: 'bank', icon: Landmark, module: 'finance', allow: 'bank.read' },
      { to: '/subscriptions', labelKey: 'subscriptions', icon: RefreshCw, allow: 'subscriptions.read' },
    ],
  },
  {
    /* The ledger itself. Set-up-once territory, and below the things touched
       daily for that reason. */
    labelKey: 'accounting',
    icon: Calculator,
    children: [
      { to: '/accounting/chart-of-accounts', labelKey: 'chart_of_accounts', icon: ListTree, module: 'finance' },
      { to: '/accounting/journal', labelKey: 'journal', icon: BookOpen, module: 'finance' },
      /*
        WHAT IS WAITING, and it is on the Accounting shelf rather than beside
        the assistant on purpose.

        A draft is not an AI feature. It is staged by the assistant, by the
        WhatsApp thread, by a spreadsheet import and by the payment correction
        dialog, and the last of those runs in a workspace with the assistant
        switched off. Filing it under the chat panel would say the opposite of
        that, and it would put the list behind the one product a tenant is
        allowed to turn off.

        It carries NO module key for the same reason the router carries no
        module gate: every workspace has changes waiting, whatever it has
        switched on. `drafts.read` is the only gate, and it is Owner, Admin,
        Finance because a preview shows a salary.
      */
      { to: '/drafts', labelKey: 'drafts', icon: FileStack, allow: 'drafts.read', badge: true },
    ],
  },
  /* Reports is what you read OFF the ledger rather than part of keeping it, and
     it is the one page in this half somebody opens on a Monday morning. It also
     survives Finance being switched off, which a shelf inside Accounting would
     not. Both reasons put it at the top level. */
  { to: '/reports', labelKey: 'reports', icon: BarChart3, allow: 'reports.read' },
  {
    labelKey: 'people',
    icon: Users,
    children: [
      { to: '/employees', labelKey: 'employees', icon: Contact, allow: 'employees.read', module: 'hr' },
      { to: '/leave', labelKey: 'leave', icon: CalendarDays, allow: 'leave.read', module: 'hr' },
      { to: '/payroll', labelKey: 'payroll', icon: CreditCard, allow: 'payroll.read', module: 'hr' },
      /*
        Your own leave carries NO `allow`, because it is not gated on a role: it
        can only ever show the person asking, and a Viewer is still somebody who
        takes holidays. It keeps the `hr` module key, because a company that does
        not run HR here has no leave for it to show.
      */
      { to: '/my-leave', labelKey: 'my_leave', icon: CalendarCheck, module: 'hr' },
    ],
  },
  /* Every document the company has, filed by the month it is from. Top level
     and no module key: every workspace has documents, and the page holding a
     company's trade licence must not disappear when Inventory does. */
  { to: '/files', labelKey: 'files', icon: FolderOpen, allow: 'files.read' },
  {
    labelKey: 'workspace',
    icon: Briefcase,
    children: [
      /*
        ONBOARDING IS NOT IN THIS LIST ANY MORE. It is a rocket in the header,
        see `Topbar`.

        The reasoning for keeping it reachable has not changed: bringing data
        across is not a day-one act, last year's books arrive in March and the
        Zoho connection gets set up when the accountant asks for it. What
        changed is where. Buried three levels down under Workspace it read as a
        setting, which is the one thing it is not, and the rail is a list of
        places the work lives rather than a list of things to do next.
      */
      /* Billing is the subscription, not configuration, and it is what an owner
         comes back to most often, so it keeps its own row rather than being two
         clicks deep inside Settings. */
      { to: '/billing', labelKey: 'settings_billing', icon: CreditCard, allow: 'settings.write' },
      { to: '/settings', labelKey: 'settings', icon: SettingsIcon, allow: 'settings.write' },
    ],
  },
];

/**
 * Which shelves are open, remembered.
 *
 * In localStorage rather than in the URL or in a context: it is a preference
 * about the furniture, it should survive a reload and a new tab, and it must
 * never be something you can send somebody a link to.
 */
const OPEN_KEY = 'railit_nav_open';

function readOpen(): Set<string> {
  try {
    const raw = localStorage.getItem(OPEN_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed)) return new Set(parsed.filter((k): k is string => typeof k === 'string'));
  } catch {
    /* Private mode, disabled storage, or a value from an older shape. Starting
       closed is a correct answer to all three, and the shelf holding the current
       page opens itself a frame later regardless. */
  }
  return new Set();
}

function writeOpen(next: Set<string>): Set<string> {
  try {
    localStorage.setItem(OPEN_KEY, JSON.stringify([...next]));
  } catch {
    /* ignore */
  }
  return next;
}

/** Mirrors NavLink's `end` semantics: /dashboard is exact, the rest match sub-routes. */
function matches(to: string, pathname: string): boolean {
  return to === '/dashboard'
    ? pathname === '/dashboard' || pathname === '/'
    : pathname === to || pathname.startsWith(`${to}/`);
}

/** What the tree looks like once the role and the products have had their say. */
type Resolved =
  | { kind: 'leaf'; leaf: NavLeaf }
  | { kind: 'shelf'; labelKey: string; icon: LucideIcon; children: NavLeaf[] };

/**
 * THE RAIL IS STRICTER THAN THE PAGE IT POINTS AT, ON PURPOSE.
 *
 * These five list pages show their own New button to everybody who can open the
 * list: there is no capability check around it, so `allow` is null in
 * createRoutes.ts and the table is right to refuse to invent one. The server is
 * not so relaxed. It enforces the write role on the POST, so a Viewer who
 * presses New today reaches the form, fills it in, and is refused on save.
 *
 * A + in the rail must not repeat that. "A + that leads to a 403 is worse than
 * no +" was the rule this was built to, and the honest capability is the one the
 * server already enforces, so that is what is named here.
 *
 * This is a plaster over a defect in five pages, not a fix for it. The pages
 * still show their own ungated New button, and they still need the same
 * capability put around it. When that happens these five entries gain a real
 * `allow` in createRoutes.ts and this table can go.
 */
const UNGATED_PAGES: Record<string, Capability> = {
  '/invoices': 'invoices.write',
  '/quotations': 'quotations.write',
  '/bills': 'bills.write',
  '/contracts': 'contracts.write',
  '/payroll': 'payroll.write',
};

/**
 * ONE ACCENT, ON THE ROW YOU ARE ON, AND NOWHERE ELSE.
 *
 * This replaces a note that said the opposite, and the note was right at the
 * time: an accent on a flat list of twenty-seven links made the navigation read
 * as a list of alerts, so it was taken out. A tree shows a handful of rows at
 * once and exactly one of them can ever be current, so the mark that says "you
 * are here" now lands once on a quiet screen instead of competing with a wall.
 *
 * The colour itself is never written here. `--sidebar-primary` is whatever
 * brand/palette.json resolves to, which is how this survives the palette moving
 * under it, and it is also the only legal way to say it: `npm run brand:check`
 * fails if a brand hex appears anywhere in the repository.
 */
const ACTIVE = cn(
  'data-[active=true]:bg-sidebar-primary data-[active=true]:font-medium',
  'data-[active=true]:text-sidebar-primary-foreground',
  'data-[active=true]:[&>svg]:text-sidebar-primary-foreground/85',
  'data-[active=true]:hover:bg-sidebar-primary data-[active=true]:hover:text-sidebar-primary-foreground',
);

/** A top level row, shared by leaves and shelves so the two cannot drift apart. */
const ROW = cn(
  'relative h-9 gap-2.5 px-2.5 text-[0.8125rem] text-sidebar-foreground/72',
  'transition-colors [&>svg]:text-sidebar-foreground/45',
  'hover:text-sidebar-foreground hover:[&>svg]:text-sidebar-foreground/70',
  ACTIVE,
);

/** A child row: the same language one step quieter and one step in. */
const SUB_ROW = cn(
  'relative h-8 gap-2.5 text-[0.8125rem] text-sidebar-foreground/68',
  'transition-colors [&>svg]:size-4 [&>svg]:text-sidebar-foreground/40',
  'hover:text-sidebar-foreground hover:[&>svg]:text-sidebar-foreground/70',
  ACTIVE,
);

/**
 * A cell in the narrow rail: the icon with its word underneath, centred.
 *
 * Taller than a row and narrower, because it is stacked. The width comes from
 * `--sidebar-width-icon`, which the shell widens past the shadcn default for
 * exactly this reason: 48px fits a glyph and nothing else, and the word is the
 * point. See Shell.tsx.
 */
const CELL = cn(
  'group/cell relative flex h-14 w-full flex-col items-center justify-center gap-1 rounded-lg px-1',
  'text-sidebar-foreground/72 transition-colors',
  'hover:bg-sidebar-accent hover:text-sidebar-foreground',
  'focus-visible:ring-2 focus-visible:ring-sidebar-ring focus-visible:outline-hidden',
);

const CELL_ACTIVE = 'bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary hover:text-sidebar-primary-foreground';

/** A row inside a flyout panel. */
const FLY_ROW = cn(
  'relative flex h-8 items-center gap-2.5 rounded-md px-2 text-[0.8125rem]',
  'text-popover-foreground/80 transition-colors [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-popover-foreground/45',
  'hover:bg-accent hover:text-popover-foreground hover:[&>svg]:text-popover-foreground/70',
  'focus-visible:ring-2 focus-visible:ring-sidebar-ring focus-visible:outline-hidden',
);

const FLY_ROW_ACTIVE = cn(
  'bg-sidebar-primary font-medium text-sidebar-primary-foreground [&>svg]:text-sidebar-primary-foreground/85',
  'hover:bg-sidebar-primary hover:text-sidebar-primary-foreground',
);

/**
 * The `+`, wherever it appears.
 *
 * Absolutely positioned against the row it belongs to, at the row's TRAILING
 * edge, which `end-1` spells correctly in both directions: in Arabic the
 * trailing edge is the left one, and `right-1` would put every create shortcut
 * on the wrong side of every row in half the product.
 */
function CreateShortcut({
  to,
  label,
  onNavigate,
  className,
}: {
  to: string;
  label: string;
  onNavigate: () => void;
  className?: string;
}) {
  return (
    <NavLink
      to={to}
      onClick={onNavigate}
      aria-label={label}
      title={label}
      className={cn(
        'absolute end-1 top-1/2 flex size-5 -translate-y-1/2 items-center justify-center rounded-[5px]',
        'transition-opacity focus-visible:ring-2 focus-visible:ring-sidebar-ring focus-visible:outline-hidden',
        className,
      )}
    >
      <Plus className="size-3.5" />
    </NavLink>
  );
}

/** How long the flyout waits before believing the pointer has really left. */
const FLYOUT_GRACE_MS = 220;

/**
 * A shelf in the narrow rail: one cell, and a panel that comes to the pointer.
 *
 * WHY A PANEL RATHER THAN EXPANDING THE RAIL. Opening the tree would move the
 * whole page sideways to answer a question about three links, and half the time
 * the answer is "not that one" and it moves back. The panel floats over the
 * content instead, so a form you are halfway through filling in does not shift
 * under your hands while you look for where to go next.
 *
 * THE DIAGONAL, which is the bug every flyout ships with. The pointer leaves the
 * cell before it reaches a row at the far end of the panel, and travelling in a
 * straight line takes it briefly over neither. Closing the instant the pointer
 * leaves the trigger therefore snaps the panel shut mid-journey, and the fix
 * that is always attempted first, closing only when the pointer enters something
 * else, leaves panels hanging open forever. A grace period costs a fifth of a
 * second and fixes both: leaving cancels nothing until the timer expires, and
 * entering either the cell or the panel cancels the timer.
 *
 * KEYBOARD REACHES IT TOO. Focus opens the panel exactly as hover does, and
 * Radix returns focus to the cell on Escape. The one thing hover must NOT do is
 * pull focus, so the auto-focus is refused unless the panel was opened
 * deliberately, or the pointer merely crossing the rail would rip the caret out
 * of whatever the person was typing.
 */
function ShelfFlyout({
  shelf,
  pathname,
  dir,
  t,
  createFor,
  onNavigate,
}: {
  shelf: { labelKey: string; icon: LucideIcon; children: NavLeaf[] };
  pathname: string;
  dir: string;
  t: (key: string) => string;
  createFor: (leaf: NavLeaf) => string | null;
  onNavigate: () => void;
}) {
  const [open, setOpen] = useState(false);
  /** True while the panel is open only because the pointer is over it. */
  const byPointer = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * THE PANEL THAT COULD NOT BE CLOSED.
   *
   * Closing hands focus back to the cell, because that is what a dismissable
   * layer owes a keyboard user. That focus then arrives at the handler that
   * opens the panel on focus, which opens it again, which is a loop with no
   * exit: pointing away reopened it, and Escape reopened it. It was open for
   * the rest of the session and nothing on the screen said why.
   *
   * So a focus that lands within a moment of a close is the panel's own
   * departing focus and is ignored. The window has to be a real one rather than
   * a microtask, because the focus is restored from an effect a commit later.
   */
  const closedJustNow = useRef(false);

  const cancelClose = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  }, []);

  const openPanel = useCallback(
    (pointer: boolean) => {
      cancelClose();
      byPointer.current = pointer;
      setOpen(true);
    },
    [cancelClose],
  );

  const closeSoon = useCallback(() => {
    cancelClose();
    timer.current = setTimeout(() => setOpen(false), FLYOUT_GRACE_MS);
  }, [cancelClose]);

  const onOpenChange = useCallback((next: boolean) => {
    if (!next) {
      closedJustNow.current = true;
      setTimeout(() => {
        closedJustNow.current = false;
      }, 300);
    }
    setOpen(next);
  }, []);

  /* A pending close on an unmounting panel would fire into nothing. */
  useEffect(() => cancelClose, [cancelClose]);

  const holdsCurrent = shelf.children.some((c) => matches(c.to, pathname));

  return (
    <SidebarMenuItem>
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            title={t(shelf.labelKey)}
            aria-label={t(shelf.labelKey)}
            onPointerEnter={() => openPanel(true)}
            onPointerLeave={closeSoon}
            /*
              Focus opens it, but only the kind of focus a person did on
              purpose. `:focus-visible` is how the browser already answers "did
              this come from the keyboard", and without asking, every click and
              every focus a library restores would count as somebody arriving.
            */
            onFocus={(e) => {
              if (!closedJustNow.current && e.currentTarget.matches(':focus-visible')) {
                openPanel(false);
              }
            }}
            onBlur={closeSoon}
            onClick={() => openPanel(false)}
            className={cn(CELL, (open || holdsCurrent) && 'bg-sidebar-accent text-sidebar-foreground')}
          >
            <shelf.icon className="size-[18px] shrink-0" />
            <span className="w-full truncate text-center text-[10px] leading-none">
              {t(shelf.labelKey)}
            </span>
            {/*
              The hint that something opens: a triangle in the corner nearest the
              panel it opens. Mirrored rather than repositioned in Arabic, because
              the panel comes out of the other side there and a corner mark that
              points away from its own panel is worse than none.
            */}
            <svg
              viewBox="0 0 6 6"
              aria-hidden
              className={cn(
                'absolute bottom-1 end-1 size-1.5 fill-current opacity-40',
                dir === 'rtl' && '-scale-x-100',
              )}
            >
              <path d="M6 0v6H0z" />
            </svg>
          </button>
        </PopoverTrigger>
        <PopoverContent
          /* Out of the rail's trailing side, which is the left one in Arabic.
             `collisionPadding` is what keeps a shelf near the foot of the window
             from opening a panel that runs off the bottom of the screen: Radix
             shifts it up rather than clipping it. */
          side={dir === 'rtl' ? 'left' : 'right'}
          align="start"
          sideOffset={6}
          collisionPadding={12}
          /* Hovering must not move the caret. A panel that opened because the
             pointer crossed a cell takes no focus on the way in and gives none
             back on the way out; one opened from the keyboard does both. */
          onOpenAutoFocus={(e) => {
            if (byPointer.current) e.preventDefault();
          }}
          onCloseAutoFocus={(e) => {
            if (byPointer.current) e.preventDefault();
          }}
          onPointerEnter={cancelClose}
          onPointerLeave={closeSoon}
          onFocusCapture={cancelClose}
          className="w-56 p-1.5"
        >
          <div className="eyebrow px-2 pt-1 pb-1.5 text-popover-foreground/45">
            {t(shelf.labelKey)}
          </div>
          <ul className="grid gap-0.5">
            {shelf.children.map((child) => {
              const active = matches(child.to, pathname);
              const create = createFor(child);
              return (
                <li key={child.to} className="group/fly relative">
                  {/* A real link rather than a handler that navigates: middle
                      click and "open in new tab" are how people compare two
                      lists, and a div with an onClick cannot do either. */}
                  <NavLink
                    to={child.to}
                    onClick={() => {
                      setOpen(false);
                      onNavigate();
                    }}
                    className={cn(FLY_ROW, active && FLY_ROW_ACTIVE, create && 'pe-8')}
                  >
                    <child.icon />
                    <span className="truncate">{t(child.labelKey)}</span>
                    {child.badge ? <DraftsBadge /> : null}
                  </NavLink>
                  {create ? (
                    <CreateShortcut
                      to={create}
                      label={`${t('nav_new')}: ${t(child.labelKey)}`}
                      onNavigate={() => {
                        setOpen(false);
                        onNavigate();
                      }}
                      className={cn(
                        active
                          ? 'text-sidebar-primary-foreground/70 opacity-100 hover:bg-sidebar-primary-foreground/15 hover:text-sidebar-primary-foreground'
                          : 'text-popover-foreground/45 opacity-0 hover:bg-border hover:text-popover-foreground focus-visible:opacity-100',
                        'group-hover/fly:opacity-100 group-focus-within/fly:opacity-100',
                      )}
                    />
                  ) : null}
                </li>
              );
            })}
          </ul>
        </PopoverContent>
      </Popover>
    </SidebarMenuItem>
  );
}

export function Sidebar() {
  const { t, dir } = useI18n();
  const { user, hasModule } = useAuth();
  const { state, isMobile, setOpenMobile, toggleSidebar } = useSidebar();
  const { pathname } = useLocation();
  const { mode, dockChat } = useAppMode();

  const collapsed = state === 'collapsed' && !isMobile;
  const tooltipSide = dir === 'rtl' ? 'left' : 'right';

  /**
   * The tree, with everything this person may not see already gone.
   *
   * A SHELF WITH ONE THING ON IT IS NOT A SHELF. When gating leaves a parent
   * holding a single child, the child is promoted to the top level and the
   * parent disappears: a tenant with Inventory switched off gets a plain
   * "Products" row rather than an "Inventory" chevron that opens to reveal one
   * item. This is the rule that keeps the module-gated views identical in
   * CONTENT to what they were before the tree, which is the thing that must not
   * regress.
   */
  const tree = useMemo<Resolved[]>(() => {
    const allowed = (leaf: NavLeaf) =>
      (!leaf.allow || can(user?.role, leaf.allow)) && (!leaf.module || hasModule(leaf.module));

    const out: Resolved[] = [];
    for (const node of NAV) {
      if (!isParent(node)) {
        if (allowed(node)) out.push({ kind: 'leaf', leaf: node });
        continue;
      }
      if (node.module && !hasModule(node.module)) continue;
      const children = node.children.filter(allowed);
      if (children.length === 0) continue;
      if (children.length === 1) out.push({ kind: 'leaf', leaf: children[0] });
      else out.push({ kind: 'shelf', labelKey: node.labelKey, icon: node.icon, children });
    }
    return out;
  }, [user?.role, hasModule]);

  /**
   * ARRIVING SOMEWHERE OPENS THE SHELF YOU ARRIVED ON.
   *
   * Following a link from an email to an invoice, or landing on /leave from the
   * agent, must not leave the rail insisting you are nowhere. So the shelf
   * holding the current route opens itself, and because it keys off the PATH
   * rather than off mounting, it covers moving between shelves inside the app as
   * well as arriving from outside it.
   *
   * It is deliberately not the reverse: nothing closes the shelf you left. A
   * rail that reshuffles itself on every navigation is one you cannot build a
   * habit on, and closing something a person opened by hand is the one thing an
   * automatic layout must never do.
   *
   * WHY THE LAST PATH IS PART OF THE STATE, rather than this being an effect.
   * Written as an effect it renders the shelf shut, commits, and then opens it,
   * which is a visible flicker on every navigation and the cascading-render
   * pattern the lint rule exists to stop. Adjusting during render is React's own
   * answer: the second pass happens before anything reaches the screen. The path
   * has to be stored for the same reason a `prev` prop is in that pattern, it is
   * what makes "we have already opened for this route" answerable, and without
   * it the shelf you close while standing in it springs open again immediately.
   *
   * ONLY A DELIBERATE OPEN IS REMEMBERED, which is why nothing is written to
   * storage here. Persisting an automatic open would mean that visiting one
   * invoice silently rewrites the preference, and Sales is still hanging open
   * next week for a reason the person never chose. The same distinction the
   * shell already draws for the collapsed rail: see Shell.tsx. Nothing is lost
   * by not saving it, because the shelf you are standing in opens itself again
   * on the way back in.
   */
  const [nav, setNav] = useState<{ open: Set<string>; path: string }>(() => ({
    open: readOpen(),
    path: '',
  }));

  if (nav.path !== pathname) {
    const shelf = tree.find(
      (node) => node.kind === 'shelf' && node.children.some((c) => matches(c.to, pathname)),
    );
    const key = shelf?.kind === 'shelf' ? shelf.labelKey : null;
    setNav({
      open: !key || nav.open.has(key) ? nav.open : new Set(nav.open).add(key),
      path: pathname,
    });
  }

  const open = nav.open;

  const toggleShelf = useCallback((key: string) => {
    setNav((prev) => {
      const next = new Set(prev.open);
      if (!next.delete(key)) next.add(key);
      return { open: writeOpen(next), path: prev.path };
    });
  }, []);

  /*
    Going to a page LEAVES agent mode.

    Agent mode replaces the whole card with the conversation, so clicking
    "Invoices" while in it used to change the route behind a screen that was
    still showing a chat: the navigation appeared to do nothing. The chat is not
    lost, it drops back to the rail beside the page you asked for, which is what
    "I want to look at something while I talk" means.

    `dockChat()`, not `setMode('manual')`. Setting the mode is only half of it:
    the rail has its own open state in the shell, so plain manual mode landed
    people on the page with the conversation nowhere on screen. Docking asks the
    shell to bring it with them.
  */
  const closeOnNavigate = useCallback(() => {
    if (isMobile) setOpenMobile(false);
    if (mode === 'agent') dockChat();
  }, [isMobile, setOpenMobile, mode, dockChat]);

  /** The row's `+`, when there is a create route and the role may use it. */
  const createFor = (leaf: NavLeaf): string | null => {
    const target = createTargetFor(leaf.to);
    if (!target?.createTo) return null;
    const needed = target.allow ?? UNGATED_PAGES[leaf.to] ?? null;
    return !needed || can(user?.role, needed) ? target.createTo : null;
  };

  const shortcut = (leaf: NavLeaf, active: boolean, scope: 'menu-item' | 'menu-sub-item') => {
    const to = createFor(leaf);
    if (!to) return null;
    return (
      <CreateShortcut
        to={to}
        label={`${t('nav_new')}: ${t(leaf.labelKey)}`}
        onNavigate={closeOnNavigate}
        className={cn(
          'text-sidebar-foreground/50 hover:bg-sidebar-border hover:text-sidebar-foreground',
          /*
            Hidden until the row is under the pointer, EXCEPT on the row you are
            on, where it stays. A column of plus signs down the whole rail reads
            as a form to fill in; one, on the list you are already looking at,
            reads as the next thing to do.

            `opacity-0` rather than `hidden` so the button keeps its place in the
            tab order: somebody navigating by keyboard reaches it, and focus
            paints it back in.
          */
          active
            ? 'text-sidebar-primary-foreground/70 opacity-100 hover:bg-sidebar-primary-foreground/15 hover:text-sidebar-primary-foreground'
            : 'opacity-0 focus-visible:opacity-100',
          scope === 'menu-item'
            ? 'group-hover/menu-item:opacity-100 group-focus-within/menu-item:opacity-100'
            : 'group-hover/menu-sub-item:opacity-100 group-focus-within/menu-sub-item:opacity-100',
        )}
      />
    );
  };

  /*
    `inset`, not the default panel: the Linear shell.

    The sidebar stops being a bordered column beside the content and instead sits
    directly on the window background, with the content floating over it as a
    rounded card. The practical difference is that the app reads as ONE surface
    with a rail on it, rather than two panels butted together with a seam down
    the middle; and the card's edge does the work the border used to, so the seam
    disappears.
  */
  return (
    <ShadcnSidebar
      side={dir === 'rtl' ? 'right' : 'left'}
      collapsible="icon"
      variant="inset"
    >
      {/*
        The mark, then the company. The tile says which product this is; the chip
        beside it says which of your companies you are looking at, and is how you
        leave it for another. The full "Railit" wordmark is gone from here on
        purpose: it spent horizontal space telling you the name of the app you
        already have open, and left no room for the one label that changes. The
        wordmark still opens the dashboard, it is just the tile.
      */}
      <SidebarHeader className="gap-0 p-0">
        <div
          className={cn(
            'flex h-14 items-center gap-1.5 border-b border-sidebar-border px-2.5',
            collapsed && 'flex-col justify-center gap-1 px-0',
          )}
        >
          <NavLink
            to="/dashboard"
            onClick={closeOnNavigate}
            title="Railit"
            aria-label="Railit"
            className="flex size-8 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-sidebar-accent"
          >
            <Logo size={22} markOnly className="shrink-0" />
          </NavLink>
          {/* The switcher, including its red countdown on a company scheduled
              for deletion. At icon width the header has room for the mark and
              nothing else, so it is dropped rather than squeezed. */}
          {collapsed ? null : <OrgSwitcher />}
        </div>
      </SidebarHeader>

      <SidebarContent className="gap-0 py-3">
        <SidebarGroup className="px-2.5 py-0">
          <SidebarGroupContent>
            <SidebarMenu className={collapsed ? 'gap-1' : 'gap-0.5'}>
              {tree.map((node) => {
                if (node.kind === 'leaf') {
                  const { leaf } = node;
                  const active = matches(leaf.to, pathname);

                  /*
                    NARROW, THE LABEL STAYS. It only gets smaller.

                    The stock icon rail drops the words and leaves nine
                    unlabelled glyphs, which is a memory test: everybody who
                    collapses it either learns the icons or gives up and expands
                    it again, and the second is what actually happens. Keeping the
                    word under the icon, truncated when it has to be, is the whole
                    reason the narrow rail is usable rather than merely narrow.
                    "Purchas..." is still a hundred times more than a shopping bag
                    on its own.
                  */
                  if (collapsed) {
                    return (
                      <SidebarMenuItem key={leaf.to}>
                        <NavLink
                          to={leaf.to}
                          onClick={closeOnNavigate}
                          end={leaf.to === '/dashboard'}
                          title={t(leaf.labelKey)}
                          className={cn(CELL, active && CELL_ACTIVE)}
                        >
                          <leaf.icon className="size-[18px] shrink-0" />
                          <span className="w-full truncate text-center text-[10px] leading-none">
                            {t(leaf.labelKey)}
                          </span>
                        </NavLink>
                      </SidebarMenuItem>
                    );
                  }

                  return (
                    <SidebarMenuItem key={leaf.to}>
                      <SidebarMenuButton
                        asChild
                        isActive={active}
                        tooltip={{ children: t(leaf.labelKey), side: tooltipSide }}
                        className={cn(ROW, createFor(leaf) && 'pe-8')}
                      >
                        <NavLink
                          to={leaf.to}
                          onClick={closeOnNavigate}
                          end={leaf.to === '/dashboard'}
                        >
                          {/* The chevron's width, spent on nothing, so a row
                              without children still lines its icon up with the
                              rows that have one. */}
                          <span className="size-3.5 shrink-0" aria-hidden />
                          <leaf.icon />
                          <span>{t(leaf.labelKey)}</span>
                        </NavLink>
                      </SidebarMenuButton>
                      {shortcut(leaf, active, 'menu-item')}
                    </SidebarMenuItem>
                  );
                }

                if (collapsed) {
                  return (
                    <ShelfFlyout
                      key={node.labelKey}
                      shelf={node}
                      pathname={pathname}
                      dir={dir}
                      t={t}
                      createFor={createFor}
                      onNavigate={closeOnNavigate}
                    />
                  );
                }

                const isOpen = open.has(node.labelKey);
                return (
                  <SidebarMenuItem key={node.labelKey}>
                    <SidebarMenuButton
                      onClick={() => toggleShelf(node.labelKey)}
                      aria-expanded={isOpen}
                      className={cn(
                        ROW,
                        /* The soft tint is the shelf saying "this is the one you
                           have open". It is deliberately weaker than the active
                           row's fill: a shelf is a container, not a place, and
                           two things at the same strength would leave nothing
                           marking where you actually are. */
                        isOpen && 'bg-sidebar-accent/50 text-sidebar-foreground',
                        '[&>svg:first-child]:size-3.5 [&>svg:first-child]:text-sidebar-foreground/40',
                      )}
                    >
                      {/*
                        Leading, as in the reference, and it points the way you
                        read: right in English, left in Arabic. Open is a quarter
                        turn clockwise from the English one, which is down in both
                        languages, so the same rotation is correct for both.
                      */}
                      <ChevronRight
                        className={cn(
                          'transition-transform duration-150',
                          isOpen ? 'rotate-90' : dir === 'rtl' && 'rotate-180',
                        )}
                      />
                      <node.icon />
                      <span>{t(node.labelKey)}</span>
                    </SidebarMenuButton>

                    {isOpen ? (
                      /* `border-s` and the margin are logical properties, so the
                         indent and its guide line are on the correct side in
                         Arabic without a second rule. */
                      <SidebarMenuSub className="mt-0.5 gap-0.5 border-sidebar-border/70">
                        {node.children.map((child) => {
                          const active = matches(child.to, pathname);
                          return (
                            <SidebarMenuSubItem key={child.to}>
                              <SidebarMenuSubButton
                                asChild
                                isActive={active}
                                className={cn(SUB_ROW, createFor(child) && 'pe-7')}
                              >
                                <NavLink to={child.to} onClick={closeOnNavigate}>
                                  <child.icon />
                                  <span className="truncate">{t(child.labelKey)}</span>
                                  {child.badge ? <DraftsBadge /> : null}
                                </NavLink>
                              </SidebarMenuSubButton>
                              {shortcut(child, active, 'menu-sub-item')}
                            </SidebarMenuSubItem>
                          );
                        })}
                      </SidebarMenuSub>
                    ) : null}
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarSeparator className="mx-0 bg-sidebar-border" />

      <SidebarFooter className="gap-1 p-2.5">
        {/*
          THE WAY INTO THE STAFF CONSOLE, FOR THE PEOPLE WHO HAVE ONE.

          There was no link to /admin anywhere in this product. Staff typed the
          URL, which is why the day it started serving the marketing catch-all
          nobody could tell whether it was broken or whether they had
          misremembered the address.

          At the foot rather than in the rail above, and never inside a
          navigation group, because it does not belong to this company: it
          leaves the workspace entirely, and every other row here stays inside
          it. It renders for nobody without the flag, so a customer never learns
          the console exists from their own sidebar.
        */}
        {user?.platform_admin ? (
          <SidebarMenu className="gap-px">
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                tooltip={{ children: 'Platform', side: tooltipSide }}
                className="h-8 gap-2.5 px-2.5 text-[0.8125rem] text-muted-foreground [&>svg]:text-muted-foreground/70"
              >
                <NavLink to="/admin" onClick={closeOnNavigate}>
                  <ShieldCheck />
                  <span>Platform</span>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        ) : null}

        {/* Credits sit directly above the account row: the two things you check
            without meaning to. See SidebarCredits for why it is a bar. */}
        <SidebarCredits />
        <SidebarSeparator className="my-1" />
        <SidebarMenu className="gap-px">
          {/* This row used to show your name and do nothing when clicked, while
              the real menu sat in the topbar. Now it IS the menu: one control,
              in the place people already reach for it. */}
          <SidebarMenuItem>
            <AccountMenu collapsed={collapsed} tooltipSide={tooltipSide} />
          </SidebarMenuItem>
          {/* Language, appearance, agent mode: the three app-level switches,
              together, at the foot of the rail. See SidebarControls. */}
          <SidebarMenuItem className="px-1 pt-1">
            <SidebarControls />
          </SidebarMenuItem>
          <SidebarMenuItem className="hidden md:block">
            <SidebarMenuButton
              onClick={toggleSidebar}
              tooltip={{
                children: collapsed ? t('expand_sidebar') : t('collapse_sidebar'),
                side: tooltipSide,
              }}
              className="h-8 gap-2.5 px-2.5 text-[0.8125rem] text-muted-foreground [&>svg]:text-muted-foreground/70"
            >
              <ChevronsLeft
                className={cn(
                  'transition-transform',
                  (collapsed ? dir === 'ltr' : dir === 'rtl') && 'rotate-180',
                )}
              />
              <span>{collapsed ? t('expand_sidebar') : t('collapse_sidebar')}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </ShadcnSidebar>
  );
}
